import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { fetchProducts, Product, ProductStock, EMPTY_STOCK } from '../utils/airtable';
import { useCart } from '../context/CartContext';
import { useCurrency } from '../context/CurrencyContext';
import { formatPrice } from '../utils/formatPrice';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import '../styles/product-detail.css';
import { toast } from 'react-toastify';

const productDetailPhotos = [
  // '/product-detail-photos/IMG_8342.JPG',
  '/product-detail-photos/IMG_8343.JPG',
  '/product-detail-photos/IMG_8344.JPG',
  '/product-detail-photos/IMG_8345.JPG',
  // '/product-detail-photos/IMG_8351.JPG',
  // '/product-detail-photos/IMG_8352.JPG',
  '/product-detail-photos/IMG_8353.JPG',
  '/product-detail-photos/IMG_8354.JPG',
  '/product-detail-photos/IMG_8355.JPG',
  '/product-detail-photos/IMG_8361.JPG',
  '/product-detail-photos/_IMG9366.JPG',
  '/product-detail-photos/_IMG9381.JPG',
  '/product-detail-photos/_IMG9398.JPG',
  '/product-detail-photos/_IMG9461.JPG',
  '/product-detail-photos/_IMG9465.JPG',
  '/product-detail-photos/_IMG9468.JPG',
  '/product-detail-photos/_IMG9470.JPG',
];

interface PriceDisplayProps {
  price: number;
  currencyCode: string;
  className?: string;
}

const PriceDisplay: React.FC<PriceDisplayProps> = ({ price, currencyCode, className = "product-price" }) => {
  const [formattedPrice, setFormattedPrice] = useState(`C$${price.toFixed(2)}`);

  useEffect(() => {
    const updatePrice = async () => {
      try {
        const formatted = await formatPrice(price, currencyCode);
        setFormattedPrice(formatted);
      } catch (error) {
        console.error('Error formatting price:', error);
        setFormattedPrice(`C$${price.toFixed(2)}`);
      }
    };

    updatePrice();
  }, [price, currencyCode]);

  return <div className={className}>{formattedPrice}</div>;
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<Product | null>(null);
  const [allProducts, setAllProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedSize, setSelectedSize] = useState<string>('');
  const [descriptionOpen, setDescriptionOpen] = useState(true);
  const [sizeChartOpen, setSizeChartOpen] = useState(true);
  const { addToCart } = useCart();
  const { currencyCode } = useCurrency();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [activeBackgroundSlide, setActiveBackgroundSlide] = useState(0);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveBackgroundSlide(
        (current) => (current + 1) % productDetailPhotos.length
      );
    }, 2500);

    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    const nextImage = new Image();
    nextImage.src =
      productDetailPhotos[
        (activeBackgroundSlide + 1) % productDetailPhotos.length
      ];
  }, [activeBackgroundSlide]);

  useEffect(() => {
    const loadProduct = async () => {
      try {
        const products = await fetchProducts();
        const found = products.find((p) => p.id === id);
        setProduct(found || null);
        setAllProducts(products.filter((p) => p.id !== id)); // Exclude current product
        if (found) {
          const firstAvailable = Object.entries(found.stock || {}).find(([, qty]) => qty > 0)?.[0];
          setSelectedSize(firstAvailable || '');
        } else {
          setSelectedSize('');
        }
      } catch (err) {
        setError('Failed to load product.');
      } finally {
        setLoading(false);
      }
    };
    loadProduct();
  }, [id]);

  const handleAddToCart = () => {
    if (!product) return;

    if (!selectedSize) {
      toast.error('Please select a valid size.');
      return;
    }

    const sizeStock = product.stock?.[selectedSize as keyof ProductStock] ?? 0;
    if (sizeStock <= 0) {
      toast.error('This size is currently out of stock.');
      return;
    }

    const success = addToCart(product, selectedSize);
    if (success) {
      toast.success(`${product.name} (Size: ${selectedSize}) added to cart!`);
    } else {
      toast.error('Failed to add product to cart. Please try again.');
    }
  };  

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="error-screen">
        <div>{error || 'Product not found.'}</div>
        <button onClick={() => navigate(-1)}>Back to Shop</button>
      </div>
    );
  }

  const stockEntries = Object.entries(product.stock || EMPTY_STOCK) as [keyof ProductStock, number][];
  const isOutOfStock = stockEntries.every(([, qty]) => qty <= 0);
  const isValidSizeSelected =
    Boolean(selectedSize) && (product.stock?.[selectedSize as keyof ProductStock] ?? 0) > 0;

  return (
    <div className="product-detail">
      <div className="product-detail-background" aria-hidden="true">
        <div className="product-detail-background-overlay" />
        <AnimatePresence initial={false}>
          <motion.img
            key={productDetailPhotos[activeBackgroundSlide]}
            src={productDetailPhotos[activeBackgroundSlide]}
            alt=""
            className="product-detail-background-image"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2, ease: 'easeInOut' }}
          />
        </AnimatePresence>
      </div>

      <div className="breadcrumb">
        <button onClick={() => navigate(-1)}>&larr; Back to Shop</button>
      </div>
      <div className="product-content">
        <div className="product-gallery">
          <div className="relative">
            <img
              src={product.imageUrls[activeImageIndex] || '/placeholder-image.jpg'}
              alt={product.name}
              className="main-image"
            />
          </div>
          <div className="thumbnail-row">
            {product.imageUrls.map((url, index) => (
              <img
                key={index}
                src={url}
                alt={`${product.name} ${index + 1}`}
                className={`thumbnail ${index === activeImageIndex ? 'active' : ''}`}
                onClick={() => setActiveImageIndex(index)}
              />
            ))}
          </div>
        </div>
        <div className="product-info">
          <div className="product-header">
            <h1 className={`product-title ${isOutOfStock ? 'opacity-70' : ''}`}>{product.name}</h1>
            <PriceDisplay price={product.price} currencyCode={currencyCode} />
          </div>

          <div className="size-selector">
            <h3>Size</h3>
            <div className="size-options">
              {stockEntries.map(([size, qty]) => {
                const isAvailable = qty > 0;
                return (
                  <button
                    key={size}
                    className={`size-button ${selectedSize === size ? 'active' : ''} ${!isAvailable ? 'unavailable opacity-40 cursor-not-allowed' : 'hover:bg-gray-200'}`}
                    onClick={() => isAvailable && setSelectedSize(size)}
                    disabled={!isAvailable}
                    title={
                      !isAvailable
                        ? `Size ${size} is out of stock`
                        : `Select size ${size} (${qty} available)`
                    }
                  >
                    {size}
                  </button>
                );
              })}
            </div>
            {!isValidSizeSelected && !isOutOfStock && (
              <p className="text-sm text-gray-600 mt-2">
                Please select a size to add to cart
              </p>
            )}
          </div>

          <button 
            className={`add-to-cart ${
              isOutOfStock || !isValidSizeSelected 
                ? 'opacity-50 cursor-not-allowed bg-gray-400' 
                : 'hover:bg-gray-800'
            }`}
            onClick={handleAddToCart}
            disabled={isOutOfStock || !isValidSizeSelected}
            title={
              isOutOfStock 
                ? 'Product is out of stock' 
                : !isValidSizeSelected 
                ? 'Please select a size' 
                : 'Add to cart'
            }
          >
            {isOutOfStock ? 'OUT OF STOCK' : 'ADD TO CART'}
          </button>

          <div className="accordion-section">
            <div className="accordion-header" onClick={() => setDescriptionOpen(!descriptionOpen)}>
              <span>DESCRIPTION</span> <span>{descriptionOpen ? '-' : '+'}</span>
            </div>
            {descriptionOpen && (
              <div className="accordion-content">
                <p>{product.description}</p>
              </div>
            )}
          </div>

          <div className="accordion-section">
            <div className="accordion-header" onClick={() => setSizeChartOpen(!sizeChartOpen)}>
              <span>SIZE CHART</span> <span>{sizeChartOpen ? '-' : '+'}</span>
            </div>
            {sizeChartOpen && (
              <div className="accordion-content">
                <p>Size chart details go here (fill in later).</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Related Products Section */}
      <div className="related-section">
        <h2>You May Also Like</h2>
        <div className="related-products">
          {allProducts.slice(0, 3).map((item) => {
            const itemOutOfStock = Object.values(item.stock || EMPTY_STOCK).every(qty => qty <= 0);
            return (
              <Link key={item.id} to={`/product/${item.id}`} className="related-item">
                <div className="image-container relative">
                  <img 
                    src={item.imageUrls[0] || '/placeholder-image.jpg'} 
                    alt={item.name}
                  />
                  {itemOutOfStock && (
                    <div className="absolute top-1 left-1 bg-red-600 text-white text-xs px-1 py-0.5 rounded text-[10px]">
                      Out of Stock
                    </div>
                  )}
                </div>
                {/* <div className="product-info">
                  <div className={`related-name ${itemOutOfStock ? 'opacity-70' : ''}`}>{item.name}</div>
                  <PriceDisplay price={item.price} currencyCode={currencyCode} className={`related-price ${itemOutOfStock ? 'opacity-70' : ''}`} />
                </div> */}
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
